import enUS from './en_US/translation.json';
import es from './es/translation.json';
import it from './it/translation.json';
import fr from './fr/translation.json';
import sr from './sr/translation.json';
import ro from './ro/translation.json';
import sq from './sq/translation.json';
import pl from './pl/translation.json';
import el from './el/translation.json';
import ar from './ar/translation.json';

export const LOCALES = [
  { code: 'en_US', intlCode: 'en-US', label: 'English', flagCode: 'GB', direction: 'ltr' },
  { code: 'es', intlCode: 'es', label: 'Español', flagCode: 'ES', direction: 'ltr' },
  { code: 'it', intlCode: 'it', label: 'Italiano', flagCode: 'IT', direction: 'ltr' },
  { code: 'fr', intlCode: 'fr', label: 'Français', flagCode: 'FR', direction: 'ltr' },
  { code: 'sr', intlCode: 'sr', label: 'Srpski', flagCode: 'RS', direction: 'ltr' },
  { code: 'ro', intlCode: 'ro', label: 'Română', flagCode: 'RO', direction: 'ltr' },
  { code: 'sq', intlCode: 'sq', label: 'Shqip', flagCode: 'AL', direction: 'ltr' },
  { code: 'pl', intlCode: 'pl', label: 'Polski', flagCode: 'PL', direction: 'ltr' },
  { code: 'el', intlCode: 'el', label: 'Ελληνικά', flagCode: 'GR', direction: 'ltr' },
  { code: 'ar', intlCode: 'ar', label: 'العربية', flagCode: 'SA', direction: 'rtl' },
] as const;

export type LocaleCode = (typeof LOCALES)[number]['code'];

/** Default UI language for new sessions and unknown/invalid locale codes. */
export const DEFAULT_LOCALE: LocaleCode = 'en_US';

const translationByLocale: Record<LocaleCode, typeof enUS> = {
  en_US: enUS,
  es,
  it,
  fr,
  sr,
  ro,
  sq,
  pl,
  el,
  ar,
};

export const isSupportedLocale = (locale?: string): locale is LocaleCode =>
  LOCALES.some(({ code, intlCode }) => locale === code || locale === intlCode);

/** Resolve any locale-like value to a supported app locale; always falls back to English. */
export const resolveLocale = (locale?: string | null): LocaleCode => {
  if (!locale) return DEFAULT_LOCALE;
  const match = LOCALES.find(
    ({ code, intlCode }) => locale === code || locale === intlCode
  );
  return match?.code ?? DEFAULT_LOCALE;
};

export const toIntlLocale = (locale?: string): string => {
  const code = resolveLocale(locale);
  return LOCALES.find(({ code: c }) => c === code)?.intlCode ?? 'en-US';
};

export const getLocaleShortCode = (locale?: string): string =>
  toIntlLocale(locale).split('-')[0].toUpperCase();

export const getLocaleFlagUrl = (flagCode: string): string =>
  `https://flagcdn.com/w20/${flagCode.toLowerCase()}.png`;

/** Text reading direction for the active locale (Arabic reads RTL). */
export const getLocaleTextDirection = (locale?: string): 'ltr' | 'rtl' => {
  const code = resolveLocale(locale);
  return LOCALES.find(({ code: c }) => c === code)?.direction ?? 'ltr';
};

/** Document layout direction — Arabic mirrors the full UI (RTL). */
export const getLocaleLayoutDirection = (locale?: string): 'ltr' | 'rtl' =>
  getLocaleTextDirection(locale);

/** @deprecated Use getLocaleTextDirection or getLocaleLayoutDirection. */
export const getLocaleDirection = getLocaleTextDirection;

export const buildI18nResources = () => {
  const resources: Record<string, { translation: typeof enUS }> = {};

  for (const { code, intlCode } of LOCALES) {
    const translation = translationByLocale[code];
    resources[code] = { translation };
    if (intlCode !== code) {
      resources[intlCode] = { translation };
    }
  }

  return resources;
};

export const SUPPORTED_INTL_LOCALES = LOCALES.flatMap(({ code, intlCode }) =>
  code === intlCode ? [intlCode] : [intlCode, code]
);
